/* eslint-disable complexity, max-lines, max-lines-per-function, max-statements */
import { useMutation, useQuery } from "@apollo/client";
import prepareComponent from "@bluframe/grapple/prepareComponent";
import { mutations, queries } from "@bluframe/mightychroma-mightytool-graphql-schema";
import socialsConfig from "config/socials";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { buildSearchParamsFromFilters, DEFAULT_FOLLOW_BACK_RATIO_MIN, getPageFromCursor, parseFiltersFromSearchParams } from "./filters";
import Prospects from "./Prospects";
import useJobTriggers from "./useJobTriggers";
const WAVE_LIMIT = 50;
const EMPTY_HANDLES = [];
const { socials: { GET_SOCIALS_HANDLES_WAVE, GET_SOCIALS_PROSPECT_DEFAULTS } } = queries;
const { socials: { SOCIALS_HANDLE_UPDATE } } = mutations;
/**
 * Wire the Socials Prospect Queue UI kit to Apollo + URL state.
 *
 * @returns {ComponentProps} UI props for the Prospects screen.
 *
 */
const usePrepareComponent = () => {
    // URL + local UI state
    const history = useHistory();
    const { pathname, search } = useLocation();
    const { activity: jobTriggerActivity, discoveryIngest, followerSync, onRunDiscoveryIngest, onRunFollowerSync } = useJobTriggers();
    const [selectedHandleId, setSelectedHandleId] = useState(null);
    const searchParams = useMemo(() => new URLSearchParams(search), [search]);
    const cursor = searchParams.get("startAt");
    useEffect(() => {
        if (searchParams.get("followBackRatioMin") !== null)
            return;
        const nextParams = new URLSearchParams(search);
        nextParams.set("followBackRatioMin", String(DEFAULT_FOLLOW_BACK_RATIO_MIN));
        history.replace({
            pathname,
            search: `?${nextParams.toString()}`
        });
    }, [history, pathname, search, searchParams]);
    // Filters from URL
    const filters = useMemo(() => parseFiltersFromSearchParams(searchParams), [searchParams]);
    // Build getSocialsHandlesWave query variables
    const query = useMemo(() => ({
        accountId: socialsConfig.ACCOUNT_ID,
        cursor: cursor ?? undefined,
        limit: WAVE_LIMIT,
        ...filters
    }), [cursor, filters]);
    // GraphQL: getSocialsHandlesWave
    const { data: getSocialsHandlesWaveData, error: getSocialsHandlesWaveError, loading: getSocialsHandlesWaveLoading, refetch: refetchSocialsHandlesWave } = useQuery(GET_SOCIALS_HANDLES_WAVE, {
        fetchPolicy: "network-only",
        variables: { query }
    });
    // GraphQL: getSocialsProspectDefaults
    const { data: getSocialsProspectDefaultsData, error: getSocialsProspectDefaultsError } = useQuery(GET_SOCIALS_PROSPECT_DEFAULTS, {
        variables: { accountId: socialsConfig.ACCOUNT_ID }
    });
    // GraphQL: socialsHandleUpdate
    const [socialsHandleUpdate, { error: socialsHandleUpdateError }] = useMutation(SOCIALS_HANDLE_UPDATE);
    const handles = getSocialsHandlesWaveData?.getSocialsHandlesWave.handles ?? EMPTY_HANDLES;
    // Selected handle (derived from current wave)
    const selectedHandle = useMemo(() => {
        if (!selectedHandleId)
            return null;
        return handles.find((handle) => handle.id === selectedHandleId) ?? null;
    }, [handles, selectedHandleId]);
    // Clear selection when paging/filtering removes the selected handle
    useEffect(() => {
        if (selectedHandleId && !selectedHandle) {
            const timeoutId = setTimeout(() => setSelectedHandleId(null), 0);
            return () => clearTimeout(timeoutId);
        }
        return undefined;
    }, [selectedHandle, selectedHandleId]);
    // Pagination link base (preserve filters; drop `startAt`)
    const paginationLink = useMemo(() => {
        const params = new URLSearchParams(search);
        params.delete("startAt");
        const queryString = params.toString();
        return queryString ? `${pathname}?${queryString}` : pathname;
    }, [pathname, search]);
    // Handlers: filters
    const onFiltersChange = useCallback((newFilters) => {
        setSelectedHandleId(null);
        const params = buildSearchParamsFromFilters(newFilters);
        const queryString = params.toString();
        history.replace({
            pathname,
            search: queryString ? `?${queryString}` : ""
        });
    }, [history, pathname]);
    // Handlers: selection + detail panel
    const onSelectHandle = (handle) => {
        setSelectedHandleId(handle.id);
    };
    const onCloseDetail = () => setSelectedHandleId(null);
    // Handlers: override mutation (optimistic)
    const onOverrideUpdate = useCallback(async (payload) => {
        if (!selectedHandle)
            return;
        const optimisticNotes = payload.notes === undefined ? selectedHandle.notes : payload.notes;
        const optimisticLatestInteractedAt = payload.latestInteractedAt === undefined
            ? selectedHandle.latestInteractedAt
            : payload.latestInteractedAt;
        try {
            await socialsHandleUpdate({
                optimisticResponse: {
                    __typename: "Mutation",
                    socialsHandleUpdate: {
                        __typename: "SocialsHandle",
                        ...selectedHandle,
                        isIgnored: payload.isIgnored ?? selectedHandle.isIgnored,
                        manualPriority: payload.manualPriority ?? selectedHandle.manualPriority,
                        latestInteractedAt: optimisticLatestInteractedAt,
                        notes: optimisticNotes
                    }
                },
                variables: {
                    socialsHandleUpdateInput: {
                        accountId: payload.accountId,
                        id: payload.handleId,
                        isIgnored: payload.isIgnored,
                        latestInteractedAt: payload.latestInteractedAt,
                        manualPriority: payload.manualPriority,
                        notes: payload.notes
                    }
                }
            });
            const didChangeLatestInteractedAt = payload.latestInteractedAt !== undefined &&
                payload.latestInteractedAt !== selectedHandle.latestInteractedAt;
            const didChangeIgnored = payload.isIgnored !== undefined &&
                payload.isIgnored !== selectedHandle.isIgnored;
            if ((didChangeLatestInteractedAt &&
                filters.latestInteractedDaysMin !== undefined) ||
                (didChangeIgnored && filters.isIgnored !== undefined)) {
                setSelectedHandleId(null);
                await refetchSocialsHandlesWave();
            }
        }
        catch {
            // ignore
        }
    }, [
        filters.isIgnored,
        filters.latestInteractedDaysMin,
        refetchSocialsHandlesWave,
        selectedHandle,
        socialsHandleUpdate
    ]);
    // Pagination label (total known only when backend reports no next page)
    const wave = getSocialsHandlesWaveData?.getSocialsHandlesWave;
    const currentPage = getPageFromCursor(cursor, WAVE_LIMIT);
    const totalPages = wave && !wave.hasNextData ? currentPage : undefined;
    return {
        discoveryIngest,
        errorMessage: getSocialsHandlesWaveError?.message ??
            getSocialsProspectDefaultsError?.message ??
            socialsHandleUpdateError?.message ??
            null,
        filters,
        followerSync,
        handles,
        isLoading: getSocialsHandlesWaveLoading,
        jobTriggerActivity,
        onCloseDetail,
        onFiltersChange,
        onOverrideUpdate,
        onRunDiscoveryIngest,
        onRunFollowerSync,
        onRetry: getSocialsHandlesWaveError
            ? () => refetchSocialsHandlesWave()
            : undefined,
        onSelectHandle,
        pagination: {
            cursor: wave?.cursor ?? null,
            hasNextData: Boolean(wave?.hasNextData),
            hasPrevData: wave?.hasPrevData ?? null,
            link: paginationLink,
            page: totalPages ? currentPage : undefined,
            prevCursor: wave?.prevCursor ?? null,
            totalPages
        },
        prospectDefaults: getSocialsProspectDefaultsData?.getSocialsProspectDefaults
            ? getSocialsProspectDefaultsData.getSocialsProspectDefaults
            : null,
        selectedHandle
    };
};
export default prepareComponent(usePrepareComponent)(Prospects);
